import exifr from 'exifr';
import { HttpStatus } from '../../../constant/responseStatus';
import { AssetService } from '../../../services/assetService';
import { InboxOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Row, Col, UploadProps, Upload, Space, Alert, Modal } from 'antd';
import IUploadAssetModel from '../../../models/request/uploadAssetRequestModel';
import {
  ExifKey,
  MIMETYPE,
  UploadFileConstants,
  UploadListType,
  UploadValidationMessages,
  MapTypeFile
} from '../../../constant/uploadFileConstants';
import { AssetType, OrientationType } from '../../../constant/assetConstants';
import { StringConstants } from '../../../constant/stringConstants';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RouteNames } from 'src/routes/routeName';
import TranslationKey from 'src/i18n/translation';
import { heicTo } from "heic-to"
import { S3FileService } from '../../../services/s3FileService';
import { UploadAssetInvalidResponse } from 'src/constant/apiResponseConstants';

const { v4: uuidv4 } = require('uuid');
var CryptoJS = require("crypto-js");

export default function FileUpload() {
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [doneFileUids, setDoneFileUids] = useState<string[]>([]);
  const navigate = useNavigate();

  let localFileListHash: string[] = [];

  const assetService = new AssetService();

  const [countUploadPerWeek, setCountUploadPerWeek] = useState(0);

  useEffect(() => {
    getCountUploadPerWeek();
  });

  const getCountUploadPerWeek = async () => {
    var response = await assetService.getCountUploadPerWeekAsync();

    if (response.data) {
      setCountUploadPerWeek(response.data);
    }
  };

  const getS3FileUrl = (fileName: string) => {
    const fileUrl = `${process.env.REACT_APP_S3_BUCKET}${fileName}`;
    return fileUrl;
  };

  const getFileType = (fileType: string) => {
    if (fileType.match(MIMETYPE.ALL_VIDEO)) {
      return AssetType.VIDEO;
    }

    return AssetType.IMAGE;
  };

  const getImageEXIF = async (imageFile: Blob) => {
    const exifData = await exifr.parse(imageFile, true).then(exifData => exifData);

    if (!exifData || exifData === undefined) {
      return null;
    }

    return exifData;
  };

  const getImageOrientation = async (exifData: any) => {
    if (exifData) {
      const imageHeight = exifData[ExifKey.HEIGHT];
      const imageWidth = exifData[ExifKey.WIDTH];

      if (imageHeight > imageWidth) {
        return OrientationType.VERTICAL;
      }
    }

    return OrientationType.HORIZONTAL;
  };

  const getValidationErrorMessage = (fileName: string, errMessage: string) => {
    return fileName + StringConstants.SPACE + errMessage;
  };

  const validateUploadFileAsync = async (
    file: File,
    onError: any,
    isHeicFormat: boolean,
    exifData: any,
    fileType: string,
    hashFile: string
  ) => {
    const fileTypeNotSupport = !(UploadFileConstants.MIME_ACCEPT.includes(file.type));

    if (!isHeicFormat && fileTypeNotSupport) {
      const errorMessage = getValidationErrorMessage(file.name, UploadValidationMessages.INVALID_FILE_TYPE);
      onError(errorMessage);

      return errorMessage;
    }

    if (fileType === AssetType.IMAGE) {
      if (file.size > (UploadFileConstants.MAX_FILE_SIZE as number)) {
        const errorMessage = getValidationErrorMessage(file.name, UploadValidationMessages.IMAGE_MAX_FILE_SIZE);
        onError(errorMessage);

        return errorMessage;
      }

      if ((exifData[ExifKey.WIDTH] * exifData[ExifKey.HEIGHT]) < (UploadFileConstants.IMAGE_MIN_WIDTH * UploadFileConstants.IMAGE_MIN_HEIGHT)) {
        const errorMessage = getValidationErrorMessage(file.name, UploadValidationMessages.IMAGE_MIN_RESOLUTION);
        onError(errorMessage);

        return errorMessage;
      }
    }

    const duplicateHash = localFileListHash.some(hash => hash === hashFile);

    if (duplicateHash) {
      const errorMessage = getValidationErrorMessage(file.name, UploadValidationMessages.FILE_ALREADY_EXISTS);
      onError(errorMessage);

      return errorMessage;
    }

    localFileListHash.push(hashFile);

    return StringConstants.EMPTY.toString();
  };

  const addNewAsset = async (
    originalFile: any,
    fileName: string,
    onError: any,
    onSuccess: any,
    onProgress: any,
    exifData: any,
    fileType: string
  ) => {
    const filePath = getS3FileUrl(fileName);
    let imageOrientation = OrientationType.HORIZONTAL;
    let imageDPI = 0;

    if (fileType === AssetType.IMAGE) {
      imageOrientation = await getImageOrientation(exifData);
      imageDPI = exifData[ExifKey.X_RESOLUTION] as number;
    }

    const model: IUploadAssetModel = {
      assetType: fileType,
      photoInfoOrientation: imageOrientation,
      originalFileInfoFileName: originalFile.name,
      originalFileInfoPath: filePath,
      originalFileInfoSize: originalFile.size,
      originalFileInfoEncoding: StringConstants.EMPTY,
      dpi: imageDPI,
      colorInfoHues: [],
      colorInfoColors: [],
      s3FilePath: fileName
    } as IUploadAssetModel;

    onProgress!({ percent: 95 });

    const response = await assetService.createAsync(model);

    // The axios interceptor resolves with error.response, which is undefined when
    // the request never got a response at all. The asset was not created, so the
    // file must not be reported as done or the artist is told the upload
    // succeeded while nothing shows up in their drafts.
    if (response === undefined) {
      onError!(TranslationKey('upload.uploadResponse.NoResponse'));

      return;
    }

    if (response.status !== HttpStatus.OK) {
      const isKnownError =
        Object.values(UploadAssetInvalidResponse)
          .includes(response.data?.failedReason);

      const errorMessage = isKnownError
        ? TranslationKey('upload.uploadResponse.' + response.data.failedReason)
        : TranslationKey('upload.uploadResponse.UnknownError');

      onError!(errorMessage);
      return;
    }

    onProgress?.({ percent: 100 });
    onSuccess!(response.data);
  };

  const getFileName = (
    fileType: string,
    originalFileName: string) => {
    const uuid1 = uuidv4().replace(/-/g, '');
    const uuid2 = uuidv4().replace(/-/g, '');

    return `${fileType.toLocaleLowerCase()}/original/${uuid1}${uuid2}/${originalFileName}`;
  };

  const convertHeicToJpg = async (originalFile: File, originalFileName: string) => {
    const jpegBlob = await heicTo(
      {
        blob: originalFile,
        type: UploadFileConstants.IMAGE_MIME_JPG.toString(),
        quality: 1
      }
    ) as Blob;

    const jpgConvertedFile = new File(
      [jpegBlob],
      `${originalFileName.replace(UploadFileConstants.IMAGE_FORMAT_HEIC, UploadFileConstants.IMAGE_FORMAT_JPG)}`,
      { type: UploadFileConstants.IMAGE_MIME_JPG.toString() }
    );

    return jpgConvertedFile;
  };

  const getHashFromFile = (file: File) => {
    var CHUNK_SIZE = 1 * 1024 * 1024; // 512KB
    var offset = 0;
    var hashObj = CryptoJS.algo['SHA256'].create();
    var fileSize = file.size;
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event: any) => {
        if (reader.error) {
          return reject(reader.error);
        }
        hashObj.update(CryptoJS.enc.Latin1.parse(reader.result));

        offset += CHUNK_SIZE;
        if (offset >= fileSize) {
          var hash = hashObj.finalize();
          var hashHex = hash.toString(CryptoJS.enc["Base64url"]);
          return resolve(hashHex);
        }
        readNext();
      };

      reader.onerror = function (err) {
        reject(err);
      };

      function readNext() {
        var fileSlice = file.slice(offset, offset + CHUNK_SIZE);
        reader.readAsBinaryString(fileSlice);
      }
      readNext();
    });
  };

  async function fixFileType(originalFile: File, originalFileName: string, newType: string, isHeicFormat: boolean) {
    if (!isHeicFormat) {
      return new File([originalFile], originalFile.name, {
        type: newType
      });
    }

    const jpegBlob = await heicTo(
      {
        blob: originalFile,
        type: UploadFileConstants.IMAGE_MIME_JPG.toString(),
        quality: 1
      }
    ) as Blob;

    const jpgConvertedFile = new File(
      [jpegBlob],
      `${originalFileName.replace(UploadFileConstants.IMAGE_FORMAT_HEIC, UploadFileConstants.IMAGE_FORMAT_JPG)}`,
      { type: UploadFileConstants.IMAGE_MIME_JPG.toString() }
    );

    return jpgConvertedFile;
  }

  const uploadProps: UploadProps = {
    multiple: true,
    listType: UploadListType.PICTURE,
    accept: UploadFileConstants.MIME_ACCEPT.toString(),
    maxCount: UploadFileConstants.MAX_UPLOAD_PER_TIME as number,
    itemRender: (originNode, file) => {
      return (
        <div>
          {originNode}
          {
            file.status === 'error'
            &&
            (
              <span className='text-danger'>
                <InfoCircleOutlined className='me-1' />
                {
                  file.error
                    ? file.error
                    : 'Error occurred while uploading'
                }
              </span>
            )
          }
        </div>
      );
    },
    showUploadList: {
      showPreviewIcon: true,
      showRemoveIcon: true,
    },
    progress: {
      strokeColor: {
        '0%': '#DBDBFF',
        '100%': '#4D4DFF',
      },
      strokeWidth: 5,
      showInfo: false,
      status: 'active',
    },
    async onChange(info) {
      const isUploading = info.fileList.some(f => f.status === 'uploading');
      const doneFiles = info.fileList.filter(f => f.status === 'done');
      const doneUids = doneFiles.map(f => f.uid);

      const newDoneUids = doneUids.filter(uid => !doneFileUids.includes(uid));

      if (!isUploading && newDoneUids.length > 0) {
        setOpenSuccessModal(true);
        setDoneFileUids(doneUids);
      }
    },
    async onRemove(fileInfo) {
      if (fileInfo['response']) {
        const fileId = fileInfo['response']['id'];
        await assetService.deleteAsync(fileId);

        setCountUploadPerWeek(countUploadPerWeek - 1);
      }
    },
    async beforeUpload(file, fileList) {
      setCountUploadPerWeek(countUploadPerWeek + fileList.length);
    },
    async customRequest({
      file,
      onError,
      onSuccess,
      onProgress,
    }) {
      let originalFile = file as File;
      const originalFileName = originalFile.name.trim();
      const isHeicFormat = originalFileName.toLocaleLowerCase().endsWith(UploadFileConstants.IMAGE_FORMAT_HEIC);

      const fileTypeFromName = originalFile.name.split('.').pop();
      const fileTypeTolowerCase = fileTypeFromName?.toLowerCase();
      const mapfileTypeFromName = MapTypeFile[fileTypeTolowerCase!];

      originalFile = await fixFileType(originalFile, originalFileName, mapfileTypeFromName!, isHeicFormat);

      const fileType = getFileType(originalFile.type);

      let exifData: any;

      if (fileType === AssetType.IMAGE) {
        exifData = await getImageEXIF(originalFile);
      }

      const hash = await getHashFromFile(originalFile);
      const errorMessage = await validateUploadFileAsync(
        originalFile,
        onError,
        isHeicFormat,
        exifData,
        fileType,
        hash + StringConstants.EMPTY);

      if (errorMessage !== StringConstants.EMPTY) {
        return;
      }

      await addFileToS3Chunks(
        originalFile,
        onError,
        onSuccess,
        onProgress,
        exifData,
        fileType);
    }
  };

  const addFileToS3Chunks = async (
    originalFile: File,
    onError: any,
    onSuccess: any,
    onProgress: any,
    exifData: any,
    fileType: string
  ) => {
    if (!originalFile) {
      return;
    }

    const fileName = getFileName(fileType, originalFile.name);
    const chunkSize = 5 * 1024 * 1024;
    const numberOfChunks = Math.ceil(originalFile.size / chunkSize);

    let parts: { partNumber: number; eTag: string }[] = [];
    let uploadId: string | undefined;

    try {
      const {
        uploadId: createdUploadId,
        partUrls
      } =
        await S3FileService.createMultipartUpload({
          fileName,
          contentType: originalFile.type,
          partCount: numberOfChunks,
        });

      uploadId = createdUploadId;

      for (let i = 0; i < numberOfChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, originalFile.size);
        const chunk = originalFile.slice(start, end);

        const eTag = await S3FileService.uploadPartToPresignedUrl(partUrls[i], chunk);
        parts.push({ eTag, partNumber: i + 1 });

        const progress = Math.min(((i + 1) / numberOfChunks) * 95, 95);

        onProgress?.({ percent: progress });
      }

      await S3FileService.completeMultipartUpload({ fileName, uploadId, parts });

      await addNewAsset(
        originalFile,
        fileName,
        onError,
        onSuccess,
        onProgress,
        exifData,
        fileType
      );
    } catch (err: any) {
      if (uploadId) {
        try {
          await S3FileService.abortMultipartUpload({ fileName, uploadId });
        } catch (_) {
          console.warn('Failed to abort multipart upload');
        }
      }

      onError?.(err.message || err);
    }
  };

  return (

    <Space direction="vertical" style={{ display: 'flex' }}>
      <Row justify="center">
        <Col xs={{ span: 24 }}>
          {
            countUploadPerWeek >= UploadFileConstants.MAX_UPLOAD_PER_WEEK
              ? <Alert message="Weekly file uploads are full." type="warning" showIcon />
              : null
          }
          <Upload.Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              {TranslationKey('upload.clickOrDrag')}
            </p>
            <p className="ant-upload-hint">
              {TranslationKey('upload.photosAndVideos')}
            </p>
          </Upload.Dragger>
        </Col>
      </Row>
      <Modal
        title={TranslationKey('upload.alert.uploadSuccessTitle')}
        open={openSuccessModal}
        okText={TranslationKey('upload.alert.goToSubmit')}
        onOk={() => navigate(RouteNames.myUpload)}
        cancelText={TranslationKey('upload.alert.uploadAgain')}
        onCancel={() => setOpenSuccessModal(false)}>
        <p>
          {TranslationKey('upload.alert.uploadSuccessDesc')}
        </p>
      </Modal>
    </Space>
  );
}